import { create } from 'zustand';
import { http } from '../api/http';

export interface StakingPosition {
  id: number;
  wallet: string;
  amountTokens: string;
  stakedAt: string;
  unstakeRequestedAt: string | null;
  unstakeCooldownEnd: string | null;
  unstakedAt: string | null;
  active: boolean;
}

export interface StakingTier {
  name: string;
  tokensRequired: string;
  multiplier: number;
}

export interface BuybackEvent {
  id: number;
  amountSolLamports: string;
  tokensBought: string;
  tokensBurned: string;
  burnSignature: string;
  notes: string | null;
  executedAt: string;
  createdAt: string;
}

interface StakingState {
  positions: StakingPosition[];
  totalStaked: string;
  tier: string;
  multiplier: number;
  tiers: StakingTier[];
  buybacks: BuybackEvent[];
  protocolStats: {
    totalBurned: string;
    totalStaked: string;
    totalRevenue: { currency: string; total: string; count: number }[];
  } | null;
  loading: boolean;

  fetchPositions: (wallet: string) => Promise<void>;
  fetchTiers: () => Promise<void>;
  fetchBuybacks: () => Promise<void>;
  fetchProtocolStats: () => Promise<void>;
  stake: (wallet: string, amountTokens: string) => Promise<void>;
  requestUnstake: (wallet: string, positionId: number) => Promise<void>;
  completeUnstake: (wallet: string, positionId: number) => Promise<void>;
}

export const useStakingStore = create<StakingState>((set) => ({
  positions: [],
  totalStaked: '0',
  tier: 'None',
  multiplier: 1.0,
  tiers: [],
  buybacks: [],
  protocolStats: null,
  loading: false,

  fetchPositions: async (wallet: string) => {
    try {
      const res = await http.get<{
        positions: StakingPosition[];
        totalStaked: string;
        tier: string;
        multiplier: number;
      }>(`/staking/${wallet}`);
      set({
        positions: res.positions,
        totalStaked: res.totalStaked,
        tier: res.tier,
        multiplier: res.multiplier,
      });
    } catch (error) {
      console.error('Failed to fetch staking positions:', error);
    }
  },

  fetchTiers: async () => {
    try {
      const res = await http.get<{ tiers: StakingTier[] }>('/staking/tiers');
      set({ tiers: res.tiers });
    } catch (error) {
      console.error('Failed to fetch staking tiers:', error);
    }
  },

  fetchBuybacks: async () => {
    try {
      const res = await http.get<{ buybacks: BuybackEvent[] }>('/protocol/buybacks');
      set({ buybacks: res.buybacks });
    } catch (error) {
      console.error('Failed to fetch buyback history:', error);
    }
  },

  fetchProtocolStats: async () => {
    try {
      const res = await http.get<{
        totalBurned: string;
        totalStaked: string;
        totalRevenue: { currency: string; total: string; count: number }[];
      }>('/protocol/stats');
      set({ protocolStats: res });
    } catch (error) {
      console.error('Failed to fetch protocol stats:', error);
    }
  },

  stake: async (wallet: string, amountTokens: string) => {
    set({ loading: true });
    try {
      const res = await http.post<{
        position: StakingPosition;
        totalStaked: string;
        tier: string;
        multiplier: number;
      }>('/staking/stake', { wallet, amountTokens });
      set((state) => ({
        positions: [res.position, ...state.positions],
        totalStaked: res.totalStaked,
        tier: res.tier,
        multiplier: res.multiplier,
        loading: false,
      }));
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  requestUnstake: async (wallet: string, positionId: number) => {
    try {
      const res = await http.post<{ position: StakingPosition }>('/staking/unstake', {
        wallet,
        positionId,
      });
      set((state) => ({
        positions: state.positions.map((p) => (p.id === positionId ? res.position : p)),
      }));
    } catch (error) {
      console.error('Failed to request unstake:', error);
      throw error;
    }
  },

  completeUnstake: async (wallet: string, positionId: number) => {
    try {
      const res = await http.post<{ position: StakingPosition }>('/staking/complete-unstake', {
        wallet,
        positionId,
      });
      set((state) => ({
        positions: state.positions.filter((p) => p.id !== positionId),
      }));
    } catch (error) {
      console.error('Failed to complete unstake:', error);
      throw error;
    }
  },
}));
